import { useCallback, useEffect, useRef, useState } from 'react';
import { getChartClickParams } from '@op/utils/getChartClickParams';
import {
  useDrilldownParams,
  EDrilldownMode,
} from '@op/hooks/useDrilldownParams';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import { SymbolType } from '@op/constants/chart';
import { LanguageType } from '@op/types/common';
import { DBO_U } from '@op/utils/format';
import { getMetricSortItem } from '../constants/topPanel';
import { useCommunityTimelineTop } from './useCommunityTimelineTop';

export interface IUseCommunityTopPanelOptions {
  enabled: boolean;
  filter: Record<string, any>;
  tab: string;
  metric: string;
  chartType: SymbolType;
  formatParamsKey?: (
    eventKeyValue: string | number | any,
    key: string,
  ) => { [key: string]: any } | string;
  eventProps: {
    key: string;
    paramsKey: { [chartType: string]: any } | string;
    defaultFilter: Record<string, any>;
    defaultProps: Record<string, any>;
    defaultConfig?: Record<string, any>;
  };
  resetDeps: unknown[];
}

export function useCommunityTopPanel({
  enabled,
  filter,
  tab,
  metric,
  chartType,
  formatParamsKey,
  eventProps,
  resetDeps,
}: IUseCommunityTopPanelOptions) {
  const {
    key: detailKey,
    paramsKey,
    defaultFilter,
    defaultProps,
    defaultConfig,
  } = eventProps;
  const { goPage } = useDrilldownParams({ jumpType: 'Window' });
  const chartRef = useRef<any>(null);
  const [visible, setVisible] = useState(false);
  const [clickParams, setClickParams] = useState<Record<string, any>>({});
  const [position, setPosition] = useState({ left: 0, top: 0 });

  const { list, loading } = useCommunityTimelineTop({
    enabled: visible && enabled,
    filter,
    tab,
    metric,
    clickParams,
  });

  const hideTipAndDownplay = useCallback(() => {
    const chart = chartRef.current;
    if (!chart) return;
    chart.dispatchAction({ type: 'hideTip' });
    chart.dispatchAction({ type: 'downplay' });
  }, []);

  const closePanel = useCallback(() => {
    setVisible(false);
    hideTipAndDownplay();
  }, [hideTipAndDownplay]);

  useEffect(() => {
    closePanel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, resetDeps);

  const resolveClickParams = useCallback(
    (event: any, paramsChartType?: string) => {
      return getChartClickParams({
        event,
        paramsKey,
        chartType,
        paramsChartType,
        dateType: defaultFilter?.date_type || filter?.date_type || 'daily',
        formatParamsKey,
      });
    },
    [
      paramsKey,
      chartType,
      defaultFilter?.date_type,
      filter?.date_type,
      formatParamsKey,
    ],
  );

  const updatePanelPosition = useCallback((event: any) => {
    const chart = chartRef.current;
    if (!chart || !event?.event) return;
    const offsetX = event.event.offsetX ?? 0;
    const offsetY = event.event.offsetY ?? 0;
    setPosition({ left: offsetX + 15, top: offsetY });
  }, []);

  const handleSeriesClick = useCallback(
    (event: any) => {
      if (!enabled) return;

      const params = resolveClickParams(event);
      buriedBtnPoint('KMCM_Top3Open', { metric, tab });
      setClickParams(params);
      updatePanelPosition(event);
      setVisible(true);

      const chart = chartRef.current;
      if (!chart) return;

      chart.dispatchAction({ type: 'hideTip' });
      chart.dispatchAction({
        type: 'highlight',
        seriesIndex: event.seriesIndex,
        dataIndex: event.dataIndex,
      });
    },
    [enabled, resolveClickParams, updatePanelPosition, metric, tab],
  );

  const bindChart = useCallback(
    (chart: any) => {
      chartRef.current = chart;

      if (!enabled) return;

      chart.getZr().on('click', (zrEvent: any) => {
        if (zrEvent?.target) return;
        closePanel();
      });
    },
    [enabled, closePanel],
  );

  const handleMore = useCallback(
    (language: LanguageType = 'origin') => {
      const sortItem = getMetricSortItem(metric); 
      goPage({
        path: '/opinion/Detail',
        // Detail 为文档标注的目标页面，走服务端缓存（失败 fallback 本地）
        mode: EDrilldownMode.Server,
        // 详情页完整参数（key/filter/title/defaultProps/extraConfig/detailPageOptions）存放在 filter 字段中
        filter: {
          key: detailKey,
          filter: { ...defaultFilter },
          title: defaultProps?.title,
          defaultProps: { ...defaultProps },
          extraConfig: {
            ...defaultConfig,
            ...clickParams,
            ...(sortItem ? { sort_item: sortItem } : {}),
            channel_type: 'social',
          },
          detailPageOptions: {
            defaultLanguages: [language],
          },
        },
        // 新窗口跳转需要带回跳地址（剥离 /v2 等部署前缀，避免回跳时匹配不到内部路由而 404）
        extra: `&redirect_path=${encodeURIComponent(
          DBO_U.stripBasePath(
            `${window.location.pathname}${window.location.search}`,
          ),
        )}`,
      });
      // `&redirect_path=${encodeURIComponent(pathname + search)}`
    },
    [
      detailKey,
      defaultFilter,
      defaultProps,
      defaultConfig,
      clickParams,
      metric,
    ],
  );

  return {
    visible,
    list,
    loading,
    clickParams,
    position,
    handleSeriesClick,
    closePanel,
    bindChart,
    handleMore,
  };
}
